import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  MessageCircle,
  X,
  Send,
  RotateCcw,
  ArrowDown,
  Zap,
} from "lucide-react";
import { personalInfo, aboutMe, skills, projects, contact } from "../mock";
import "./ChatAssistant.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SECTION_IDS = ["about", "skills", "projects", "experience", "education", "contact"];

const scrollToSection = (section) => {
  const normalized = typeof section === "string" ? section.trim().toLowerCase() : "";
  if (!SECTION_IDS.includes(normalized)) return;
  const el = document.getElementById(normalized);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 72;
  window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  el.classList.remove("chat-section-flash");
  void el.offsetWidth;
  el.classList.add("chat-section-flash");
  window.setTimeout(() => el.classList.remove("chat-section-flash"), 2600);
};

const QUICK_REPLIES = [
  "Who is Adeel?",
  "What skills does he have?",
  "Show me his projects",
  "How can I hire him?",
];

const GREETING =
  "Hi! 👋 I'm Adeel's AI assistant — ask me about his projects, skills, education, or how to get in touch. What would you like to know?";

let idCounter = 0;
const nextId = () => `m${++idCounter}_${Date.now()}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SOCIALS =
  `• Email: ${personalInfo.email}\n` +
  `• LinkedIn: ${personalInfo.linkedin}\n` +
  `• GitHub: ${personalInfo.github}`;

function greetLike(name) {
  return name ? `Nice to meet you, ${name}! ` : "";
}

function buildTechReplies(name) {
  const g = greetLike(name);
  return {
    langchain: `${g}LangChain & LangGraph are his staples. He builds multi-step agent workflows with them — the AI Coder & Reviewer generates, reviews and debugs code in real time, and his RAG systems retrieve over FAISS embeddings.`,
    langgraph: `${g}LangGraph powers his agent pipelines — same story as LangChain: multi-step, stateful AI workflows like the AI Coder & Reviewer.`,
    rag: `${g}RAG is one of his specialty areas. Check out "RAG PDF Insights": multi-document retrieval with LangChain, Llama 3, FAISS embeddings, and n8n workflow automation.`,
    mcp: `${g}He designs custom MCP servers — for example a LinkedIn post generator and an AI HR assistant that shortlists candidates with n8n + classifiers.`,
    agent: `${g}Agentic AI is his sweet spot. He builds production-ready AI agents for inbound voice calls (LiveKit + Twilio), HR processing, code review, and automation.`,
    voice: `${g}His Hospital AI Voice Receptionist answers real inbound calls, books appointments, checks doctor availability, and routes emergencies — built with LiveKit Agents, Twilio Voice, Deepgram STT, and Cartesia TTS.`,
    tensorflow: `${g}Deep learning is in his toolkit too — he trained a CNN brain-tumor detector with ~94% accuracy using TensorFlow & Keras.`,
    keras: `${g}He uses Keras/TensorFlow for deep learning — e.g. his CNN-based brain tumor detection model at 94% accuracy.`,
    streamlit: `${g}Streamlit is his go-to for fast ML demos — like the brain tumor detector and multiple disease prediction apps.`,
    n8n: `${g}n8n is his automation engine — used for AI HR candidate shortlisting and RAG workflow orchestration.`,
    faiss: `${g}FAISS + embeddings fuel his RAG pipelines for fast, accurate multi-document retrieval.`,
    embeddings: `${g}He works extensively with vector embeddings and FAISS for semantic search inside his RAG systems.`,
    fastapi: `${g}FastAPI is his preferred backend for AI services — it powers his AI Coder & Reviewer and voice receptionist APIs.`,
    mongodb: `${g}MongoDB is his database of choice for AI apps — used by the voice receptionist, AI Coder, and the university LMS.`,
    python: `${g}Python is his primary language — driving all his ML, AI, and automation projects.`,
    twilio: `${g}Twilio Voice + LiveKit power his Hospital AI Voice Receptionist for real phone call handling.`,
  };
}

function getReply(raw) {
  const t = ` ${raw.toLowerCase()} `;
  let name = "";
  const nameMatch = raw.match(/\b(?:my name is|i am|i'm|call me)\s+([a-z]+)/i);
  if (nameMatch) {
    name = nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
  }
  const g = greetLike(name);
  const has = (...kws) => kws.some((k) => t.includes(k));
  const tech = buildTechReplies(name);

  const techHit = Object.keys(tech).find((k) => t.includes(k) || has(k));
  if (techHit) {
    return {
      text: tech[techHit],
      chips: has("voice", "hospital")
        ? ["Who is Adeel?", "More projects", "Hire Adeel"]
        : ["Show me his projects", "Hire Adeel"],
    };
  }

  if (has("how are you")) {
    return {
      text: `${g}Running at full capacity! ⚡ I'm Adeel's AI assistant, always online. Ask me about his work, skills, or how to reach him.`,
      chips: QUICK_REPLIES,
    };
  }

  if (has("hello", "hi", "hey", "salam", "assalam", "good morning", "good afternoon", "good evening", "hola", "yo")) {
    return {
      text: `${g}Hey there! 👋 I'm Adeel's AI assistant. Ask me about his projects, skills, education, or how to hire him.`,
      chips: QUICK_REPLIES,
    };
  }

  if (has("thank", "thanks", "shukria", "appreciate")) {
    return {
      text: "Anytime! 😊 Want me to dig into anything else — projects, skills, or contact details?",
      chips: ["Show me his projects", "What skills does he have?", "Hire Adeel"],
    };
  }

  if (has("bye", "goodbye", "see you", "that's all", "that is all")) {
    return {
      text: "Thanks for stopping by! 🙌 Click the button anytime you need me again. Take care!",
      chips: null,
    };
  }

  if (has("who", "about", "introduce", "yourself", "tell me about", "describe", "background", "bio", "summary")) {
    return {
      text:
        `${g}Adeel Ahmed is a passionate ${personalInfo.title} specializing in intelligent automation and business intelligence.\n\n` +
        `${aboutMe.description}\n\nHighlights:\n` +
        aboutMe.highlights.map((h) => `• ${h}`).join("\n"),
      chips: ["What skills does he have?", "Show me his projects", "How can I hire him?"],
    };
  }

  if (has("skill", "tech stack", "tech ", "stack", "languages", "frameworks", "tool", "capable", "knows", "technolog", "proficiency")) {
    const lines = Object.entries(skills)
      .map(([group, items]) => `${group}: ${items.join(", ")}`)
      .join("\n\n");
    return {
      text: `${g}Here's Adeel's tech arsenal:\n\n${lines}\n\nAsk me about any of them — try "LangChain", "RAG", "voice", or "deep learning".`,
      chips: ["LangChain", "RAG", "Voice AI", "Deep learning", "Hire Adeel"],
    };
  }

  if (has("project", "portfolio", "work", "built", "created", "developed", "case study", "case studies", "demos", "products", "what did")) {
    const top = projects.slice(0, 4);
    return {
      text:
        `${g}Here are a few highlights from his portfolio:\n\n` +
        top.map((p) => `• ${p.title} — ${p.description}`).join("\n\n") +
        `\n\nAsk me about any of them for more detail!`,
      chips: ["Hospital AI Receptionist", "RAG PDF Insights", "AI Coder & Reviewer", "Brain tumor detection"],
    };
  }

  if (has("hospital", "receptionis", "voice", "phone call", "caller", "sip", "twilio", "livekit")) {
    const p = projects.find((x) => /hospital|voice/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\nFeatures:\n${p.features.map((f) => `• ${f}`).join("\n")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "What is RAG?", "Hire Adeel"],
    };
  }

  if (has("coder", "code review", "debug", "codegen", "code gen")) {
    const p = projects.find((x) => /coder/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\nFeatures:\n${p.features.map((f) => `• ${f}`).join("\n")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "What about RAG?", "Hire Adeel"],
    };
  }

  if (has("pdf", "document", "rag", "retriev", "insight")) {
    const p = projects.find((x) => /rag/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\nFeatures:\n${p.features.map((f) => `• ${f}`).join("\n")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["What is MCP?", "Show me his projects", "Hire Adeel"],
    };
  }

  if (has("brain", "tumor", "mri", "cnn", "medical imaging")) {
    const p = projects.find((x) => /brain/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\nFeatures:\n${p.features.map((f) => `• ${f}`).join("\n")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "Multiple disease detection", "Hire Adeel"],
    };
  }

  if (has("hr", "candidate", "shortlist", "resume analysis", "hiring", "recruit")) {
    const p = projects.find((x) => /hr/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "Hire Adeel"],
    };
  }

  if (has("linkedin post", "post generator", "content creation", "content gen")) {
    const p = projects.find((x) => /linkedin/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "Hire Adeel"],
    };
  }

  if (has("lms", "university", "learning management")) {
    const p = projects.find((x) => /lms/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "Hire Adeel"],
    };
  }

  if (has("disease", "diabet", "parkinson", "heart", "multiple disease")) {
    const p = projects.find((x) => /disease/i.test(x.title));
    return {
      text: `${g}${p.title} — ${p.description}\n\nTechnologies: ${p.technologies.join(", ")}\n\n🔗 Demo: ${p.demo}`,
      chips: ["Show me his projects", "Brain tumor detection", "Hire Adeel"],
    };
  }

  if (has("educat", "degree", "study", "university", "college", "batch", "graduate", "bscs", "fsc")) {
    return {
      text:
        `${g}Here's his academic background:\n\n` +
        educationText + 
        `\n\nCurrently pursuing BSCS (Specializing in AI & ML), graduating 2027.`,
      chips: ["What skills does he have?", "Hire Adeel"],
    };
  }

  if (has("resume", "cv", "curriculum", "download", "cv/")) {
    return {
      text: `Sure! You can grab his resume at ${personalInfo.resume} — or browse his work on GitHub: ${personalInfo.github} 📄`,
      chips: ["Show me his projects", "How can I hire him?"],
    };
  }

  if (has("location", "where", "city", "from", "based", "live")) {
    return {
      text: `${g}Adeel is based in ${personalInfo.location} and works with clients worldwide 🌍`,
      chips: ["How can I hire him?", "Who is Adeel?"],
    };
  }

  if (has("github", "git")) {
    return {
      text: `Here's his GitHub: ${personalInfo.github} 🚀\n\nYou'll find all his AI/ML projects and open-source contributions there.`,
      chips: ["Show me his projects", "LinkedIn"],
    };
  }

  if (has("linkedin", "social", "follow", "connect")) {
    return {
      text: `Connect with Adeel on LinkedIn: ${personalInfo.linkedin} 🤝`,
      chips: ["Who is Adeel?", "How can I hire him?"],
    };
  }

  if (has("experience", "senior", "junior", "years", "level", "profile exp")) {
    return {
      text: `${g}Adeel has 10+ production-ready AI/ML projects, and while still finishing his BSCS he already ships real-world systems — voice AI, RAG platforms, agentic workflows, and full-stack apps.`,
      chips: ["Who is Adeel?", "Show me his projects"],
    };
  }

  if (has("available", "availability", "open to", "freelance", "opportunity")) {
    return {
      text: `${g}Great news — ${contact.availability.toLowerCase()}! 🎉\n\n${SOCIALS}\n\nHe usually responds within 24–48 hours.`,
      chips: ["How can I hire him?", "Who is Adeel?"],
    };
  }

  if (has("phone", "number", "call", "whatsapp", "whats app")) {
    return {
      text: `You can reach Adeel at ${personalInfo.phone} 📞 (based in Pakistan, open to global work).`,
      chips: ["How can I hire him?"],
    };
  }

  if (has("email", "mail", "inbox", "reach", "contact", "touch", "message him", "get in touch", "communicate")) {
    return {
      text: `${g}Best way to reach Adeel:\n\n${SOCIALS}\n\nHe typically replies within 24–48 hours and is open to freelance and full-time roles.`,
      chips: ["How can I hire him?", "Who is Adeel?"],
    };
  }

  if (has("hire", "recruit", "work with", "collaborate", "opportunity", "job", "role", "interview", "employ")) {
    return {
      text:
        `${g}Adeel is ${contact.availability.toLowerCase()}.\n\n` +
        `To hire or collaborate:\n\n${SOCIALS}\n\n` +
        `Tip: mention the role/project and timeline — he loves a well-scoped challenge. 💼`,
      chips: ["Download resume", "Who is Adeel?", "Show me his projects"],
    };
  }

  if (has("cost", "price", "rate", "charge", "pricing", "budget")) {
    return {
      text: `Pricing depends on scope — best to reach out directly. 😊\n\n${SOCIALS}\n\nShare your project details and he'll get back with a tailored quote.`,
      chips: ["How can I hire him?", "Download resume"],
    };
  }

  return {
    text:
      `${g}Hmm, I'm not sure about that one! 🤔 I'm best at answering questions like:\n\n` +
      `• Who is Adeel?\n• What are his skills?\n• About his projects\n• Education & background\n• How to hire or contact him`,
    chips: QUICK_REPLIES,
  };
}

const educationText =
  `• BSCS — Institute of Space Technology (KICSIT Campus), 2023–2027\n` +
  `• FSC (ICS) — Military College Murree, 2020–2022`;

function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [chips, setChips] = useState(QUICK_REPLIES);
  const [input, setInput] = useState("");
  const [showGreetingPill, setShowGreetingPill] = useState(false);
  const [showJump, setShowJump] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const streamingRef = useRef(false);
  const stickRef = useRef(true);

  const formatTime = (d) =>
    d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const scrollToBottom = (force = false) => {
    const el = scrollRef.current;
    if (!el) return;
    if (force || stickRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
    stickRef.current = nearBottom;
    setShowJump(!nearBottom);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing, chips, open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setShowGreetingPill(true), 1700);
      return () => clearTimeout(t);
    }
    setShowGreetingPill(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeChat();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open && !closing) {
      const t = setTimeout(() => inputRef.current?.focus(), 360);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, closing]);

  const startBotReply = async (text, nextChips = null) => {
    if (streamingRef.current) return;
    streamingRef.current = true;
    setStreaming(true);
    setTyping(true);
    setChips(null);

    const think = 300 + Math.random() * 250;
    await sleep(think);
    setTyping(false);

    let botId = null;
    let revealed = false;
    const step = text.length > 380 ? 8 : text.length > 180 ? 5 : 3;
    const delay = Math.max(7, Math.min(16, 52 / step));

    for (let i = step; i <= text.length; i += step) {
      if (!revealed) {
        botId = nextId();
        setMessages((m) => [
          ...m,
          { id: botId, sender: "bot", text: "", done: false, time: new Date() },
        ]);
        revealed = true;
      }
      setMessages((m) =>
        m.map((msg) =>
          msg.id === botId ? { ...msg, text: text.slice(0, i) } : msg,
        ),
      );
      await sleep(delay);
    }

    setMessages((m) =>
      m.map((msg) =>
        msg.id === botId ? { ...msg, text, done: true } : msg,
      ),
    );
    if (!revealed) {
      const id = nextId();
      setMessages((m) => [
        ...m,
        { id, sender: "bot", text, done: true, time: new Date() },
      ]);
    }
    streamingRef.current = false;
    setStreaming(false);
    if (nextChips?.length) {
      setTimeout(() => setChips(nextChips), 220);
    }
  };

  const openChat = () => {
    setShowGreetingPill(false);
    setClosing(false);
    setOpen(true);
    if (messages.length === 0) {
      setTimeout(() => startBotReply(GREETING, QUICK_REPLIES), 430);
    }
  };

  const closeChat = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 280);
  };

  const resetChat = () => {
    if (streamingRef.current) return;
    setMessages([]);
    setTyping(false);
    setStreaming(false);
    setInput("");
    setChips(QUICK_REPLIES);
    stickRef.current = true;
    setTimeout(() => startBotReply(GREETING, QUICK_REPLIES), 150);
  };

  const consumeStream = async (res) => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let botId = null;
    let full = "";
    streamingRef.current = true;
    setStreaming(true);
    setTyping(true);
    setChips(null);

    const push = (text, done) => {
      if (!botId) {
        botId = nextId();
        setMessages((m) => [
          ...m,
          { id: botId, sender: "bot", text, done, time: new Date() },
        ]);
      } else {
        setMessages((m) =>
          m.map((msg) => (msg.id === botId ? { ...msg, text, done } : msg)),
        );
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!block) continue;

          const line = block.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;

          let payload;
          try {
            payload = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }

          if (payload.event === "path") {
            scrollToSection(payload.data);
          } else if (payload.event === "token") {
            if (payload.data) {
              setTyping(false);
              full += payload.data;
              push(full, false);
            }
          } else if (payload.event === "done") {
            push(full, true);
          } else if (payload.event === "error") {
            throw new Error(payload.data);
          }
        }
      }
      push(full, true);
    } finally {
      streamingRef.current = false;
      setStreaming(false);
      setTyping(false);
    }
  };

  const send = (preset) => {
    const value = (preset ?? input).trim();
    if (!value || streamingRef.current) return;

    setMessages((m) => [
      ...m,
      { id: nextId(), sender: "user", text: value, time: new Date() },
    ]);
    setInput("");
    setChips(null);
    stickRef.current = true;

    const ask = async () => {
      if (BACKEND_URL) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: value }),
          });
          if (res.ok && res.body) {
            await consumeStream(res);
            return;
          }
        } catch (err) {
          console.error("Chat stream error:", err);
        }

        try {
          const res = await fetch(`${BACKEND_URL}/api/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: value }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data && typeof data.response === "string") {
              scrollToSection(data.path);
              startBotReply(data.response, null);
              return;
            }
          }
        } catch (err) {
          console.error("Chat API error:", err);
        }
      }
      const { text, chips: nextChips } = getReply(value);
      startBotReply(text, nextChips);
    };

    ask();
  };

  const handleInput = (e) => {
    const el = e.target;
    setInput(el.value);
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="chat-assistant" aria-live="polite">
      {open && (
        <div
          className={`chat-window ${closing ? "is-closing" : ""}`}
          role="dialog"
          aria-modal="false"
          aria-label="Chat with Adeel's AI assistant"
        >
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-name">Ask about Adeel</div>
            </div>
            <div className="chat-header-actions">
              <button
                className="chat-icon-btn cursor-target"
                onClick={resetChat}
                title="Reset conversation"
                aria-label="Reset conversation"
              >
                <RotateCcw size={15} />
              </button>
              <button
                className="chat-icon-btn cursor-target"
                onClick={closeChat}
                title="Close"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="chat-messages" ref={scrollRef} onScroll={handleScroll}>
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-msg ${msg.sender}`}>
                {msg.sender === "bot" && (
                  <div className="chat-msg-avatar">
                    <Sparkles size={14} />
                  </div>
                )}
                <div className="chat-bubble-wrap">
                  <div className="chat-bubble">
                    {msg.text}
                    {msg.sender === "bot" && msg.done === false && (
                      <span className="chat-caret-mark" />
                    )}
                  </div>
                  <span className="chat-time">{formatTime(msg.time)}</span>
                </div>
              </div>
            ))}

            {typing && (
              <div className="chat-msg bot">
                <div className="chat-msg-avatar">
                  <Sparkles size={14} />
                </div>
                <div className="chat-bubble chat-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          {showJump && (
            <button
              className="chat-jump cursor-target"
              onClick={() => scrollToBottom(true)}
              aria-label="Jump to latest message"
            >
              <ArrowDown size={15} />
            </button>
          )}

          <div className="chat-input-wrap">
            <div className="chat-input-row">
              <textarea
                ref={inputRef}
                className="chat-input cursor-target"
                rows={1}
                placeholder="Message Adeel's AI assistant…"
                value={input}
                onChange={handleInput}
                onKeyDown={handleKey}
              />
              <button
                className="chat-send cursor-target"
                onClick={() => send()}
                disabled={!input.trim() || streaming || typing}
                aria-label="Send message"
              >
                {streaming ? (
                  <Sparkles className="chat-send-spin" size={19} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </div>
            <div className="chat-footnote">
              <Zap size={10} />
              Powered by agentic AI &middot; answers from Adeel&apos;s portfolio
            </div>
          </div>
        </div>
      )}

      {!open && showGreetingPill && (
        <button className="chat-greeting cursor-target" onClick={openChat} type="button">
          <div className="chat-greeting-title">
            <Sparkles size={13} />
            Ask about Adeel
          </div>
        </button>
      )}

      <button
        className={`chat-launcher cursor-target ${open ? "chat-is-open" : ""}`}
        onClick={open ? closeChat : openChat}
        aria-label={open ? "Close chat" : "Open chat assistant"}
        type="button"
      >
        <span className="chat-ring" />
        <span className="chat-ring chat-ring--2" />
        <span className="chat-badge">
          <Zap size={10} fill="currentColor" />
        </span>
        <span key={open ? "x" : "msg"} className="chat-icon chat-icon-pop">
          {open ? <X size={25} /> : <MessageCircle size={25} />}
        </span>
      </button>
    </div>
  );
}

export default ChatAssistant;